package com.htmlagent.site.components.sections.landing

import androidx.compose.runtime.Composable
import com.htmlagent.site.components.sections.Glyph
import com.htmlagent.site.components.widgets.reveal
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.AccentLineStyle
import com.htmlagent.site.theme.DisplayTitleStyle
import com.htmlagent.site.theme.EyebrowStyle
import com.htmlagent.site.theme.HeroActionsStyle
import com.htmlagent.site.theme.HeroArtStyle
import com.htmlagent.site.theme.HeroBadgeDotStyle
import com.htmlagent.site.theme.HeroBadgeStyle
import com.htmlagent.site.theme.HeroFactDotStyle
import com.htmlagent.site.theme.HeroFactStyle
import com.htmlagent.site.theme.HeroFactsStyle
import com.htmlagent.site.theme.HeroFrameAddressStyle
import com.htmlagent.site.theme.HeroFrameBarStyle
import com.htmlagent.site.theme.HeroFrameStyle
import com.htmlagent.site.theme.HeroGlowStyle
import com.htmlagent.site.theme.HeroStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.LandingInnerStyle
import com.htmlagent.site.theme.LandingLeadStyle
import com.htmlagent.site.theme.MockBannerStyle
import com.htmlagent.site.theme.MockCardStyle
import com.htmlagent.site.theme.MockCardsStyle
import com.htmlagent.site.theme.MockLineStyle
import com.htmlagent.site.theme.MockPageStyle
import com.htmlagent.site.theme.PillGhostVariant
import com.htmlagent.site.theme.PillPrimaryVariant
import com.htmlagent.site.theme.TrafficLightStyle
import com.htmlagent.site.theme.TrafficLightsStyle
import com.htmlagent.site.theme.Tokens
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** The three lights on the mock-up's title bar. A count, not a measurement: it stays here. */
private const val TRAFFIC_LIGHTS = 3

/** The cards the mock page draws under its banner. */
private const val MOCK_CARDS = 3

/** How wide each fake line is drawn, as a percentage of its row. */
private val MOCK_LINE_WIDTHS = listOf("62%", "88%", "45%")

/**
 * The first screen: what the product is, in two lines, and a page being built beside them.
 *
 * The headline is split in two so the second half can carry the accent. That is the page's one
 * unmissable use of color, and it lands on the word the whole product is about.
 *
 * The visual on the right is drawn in CSS and holds no image on purpose: the product's claim is
 * that it builds pages out of nothing but markup and styles, and a stock photograph here would
 * be arguing the other way on the very first screen.
 */
@Composable
fun LandingHero(onStart: () -> Unit, onLearnMore: () -> Unit) {
    val t = LocalStrings.current

    Section(
        attrs = {
            attr("id", SECTION_HERO)
            attr("aria-labelledby", headingId(SECTION_HERO))
        },
    ) {
        Div(LandingInnerStyle.toModifier().then(HeroStyle.toModifier()).toAttrs()) {
            Div(Modifier.reveal().toAttrs()) {
                Span(HeroBadgeStyle.toModifier().toAttrs()) {
                    Span(HeroBadgeDotStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
                    Span(EyebrowStyle.toModifier().toAttrs { attr("data-tone", "accent") }) {
                        Text(t.landing.heroEyebrow)
                    }
                }

                H1(
                    DisplayTitleStyle.toModifier().toAttrs {
                        attr("id", headingId(SECTION_HERO))
                        attr("data-size", "hero")
                    },
                ) {
                    Text(t.landing.heroLineOne)
                    // The two halves are separate elements, and a screen reader joins a
                    // heading's descendants with nothing between them: without this the
                    // headline is announced as one run-together word.
                    Text(" ")
                    Span(AccentLineStyle.toModifier().toAttrs()) { Text(t.landing.heroLineTwo) }
                }

                P(LandingLeadStyle.toModifier().toAttrs()) { Text(t.landing.heroSubtitle) }

                Div(HeroActionsStyle.toModifier().toAttrs()) {
                    Button(onClick = { onStart() }, variant = PillPrimaryVariant) {
                        Text(t.landing.heroPrimary)
                        Glyph(Icons.ArrowRight, Tokens.iconSm.value())
                    }
                    Button(onClick = { onLearnMore() }, variant = PillGhostVariant) {
                        Text(t.landing.heroSecondary)
                    }
                }

                Ul(HeroFactsStyle.toModifier().toAttrs()) {
                    t.landing.heroFacts.forEach { fact ->
                        Li(HeroFactStyle.toModifier().toAttrs()) {
                            Span(HeroFactDotStyle.toModifier().toAttrs { attr("aria-hidden", "true") })
                            Text(fact)
                        }
                    }
                }
            }

            HeroArt()
        }
    }
}

/**
 * The browser frame that floats beside the headline, with its light behind it.
 *
 * The whole thing is decorative — it says the same as the copy next to it — so it carries one
 * accessible name and nothing inside it is announced separately.
 */
@Composable
private fun HeroArt() {
    val t = LocalStrings.current

    Div(HeroArtStyle.toModifier().reveal(STAGGER_MS).toAttrs()) {
        Div(HeroGlowStyle.toModifier().toAttrs { attr("aria-hidden", "true") })

        Div(
            HeroFrameStyle.toModifier().toAttrs {
                attr("role", "img")
                attr("aria-label", t.landing.heroFrameLabel)
            },
        ) {
            Div(HeroFrameBarStyle.toModifier().toAttrs()) {
                Row(TrafficLightsStyle.toModifier()) {
                    repeat(TRAFFIC_LIGHTS) { Div(TrafficLightStyle.toModifier().toAttrs()) }
                }
                Div(HeroFrameAddressStyle.toModifier().toAttrs()) { Text(t.landing.heroFrameAddress) }
            }

            Div(MockPageStyle.toModifier().toAttrs()) {
                Div(MockBannerStyle.toModifier().toAttrs()) {
                    MockLine(width = "70%", onAccent = true)
                    MockLine(width = "44%", onAccent = true)
                }
                MOCK_LINE_WIDTHS.forEach { MockLine(width = it) }
                Div(MockCardsStyle.toModifier().toAttrs()) {
                    repeat(MOCK_CARDS) { Div(MockCardStyle.toModifier().toAttrs()) }
                }
            }
        }
    }
}

/** One line of fake text inside the mock page. */
@Composable
private fun MockLine(width: String, onAccent: Boolean = false) {
    Div(
        MockLineStyle.toModifier()
            .styleModifier { property("width", width) }
            .toAttrs { if (onAccent) attr("data-on-accent", "true") },
    )
}
